import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Divider, Text } from '~/commonComponents';
import { Accordion, AccordionItem } from '~/commonComponents/Accordion';
import { DeclineCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview/components/DeclineCandidatePopups/DeclineCandidateConfirmationPopup';
import { DeclineCandidateSuccessPopup } from '~/components/PermanentJobsV2/JobOverview/components/DeclineCandidatePopups/DeclineCandidateSuccessPopup';
import {
  CandidateStatus,
  OnsiteInterviewStatus,
  useGetInvitableOnsiteInterviews,
} from '~/domain/PermanentJobs';
import { Colors } from '~/themes/colors';
import { EducationUtils } from '~/utils/EducationUtils';
import { InterviewDetailsModal } from '../../components/InterviewDetailsModal';
import { OnsiteInterviewCard } from '../../components/OnsiteInterviewCard';
import { useConfirmInterview, useDeclineInterview, useInviteToInterview } from '../../hooks';
import type { IOnsiteInterviewsPanelProps } from './interfaces';
import { Title } from './Title';

export const OnsiteInterviewsPanel = ({
  postingId,
  groups,
  maps,
  officeId,
  officeStatus,
  professionalId,
  professionalName,
}: IOnsiteInterviewsPanelProps) => {
  const navigate = useNavigate();

  const {
    isConfirming,
    isOpen: isConfirmOpen,
    selectedInterviewId: confirmInterviewId,
    handleCloseConfirmInterview,
    handleConfirmCandidateInterview,
    handleConfirmInterview,
  } = useConfirmInterview({ professionalId, officeId });
  const {
    isDeclineSuccess,
    isDeclining,
    isOpen: isDeclineOpen,
    handleCloseDeclineInterview,
    handleConfirmDeclineInterview,
    handleDeclineInterview,
    selectedInterviewId: declineInterviewId,
  } = useDeclineInterview({ professionalId });
  const { handleInvite, getIsLoadingById: getIsInvitingById } = useInviteToInterview({
    officeId,
    postingId,
    professionalId,
  });

  const { data: invitableInterviews = [] } = useGetInvitableOnsiteInterviews({
    postingId,
    professionalId,
  });
  const appliedIds = groups[OnsiteInterviewStatus.ActionNeeded] ?? [];
  const scheduledIds = groups[OnsiteInterviewStatus.Scheduled] ?? [];
  const completedIds = groups[OnsiteInterviewStatus.Completed] ?? [];

  const isTerminalStatus =
    officeStatus === CandidateStatus.Hired || officeStatus === CandidateStatus.Rejected;

  const defaultOpenKey = useMemo(() => {
    if (isTerminalStatus) {
      return 'completed';
    }

    const hasActionNeededItems = appliedIds.length > 0;

    switch (officeStatus) {
      case CandidateStatus.Applied:
        return hasActionNeededItems ? 'applied' : 'available';
      case CandidateStatus.AppExpired:
        return 'available';
      case CandidateStatus.Scheduled:
        return 'scheduled';
      case CandidateStatus.Interviewed:
        return 'completed';
      default:
        return 'available';
    }
  }, [isTerminalStatus, appliedIds.length, officeStatus]);

  const handleMessageClick = (id: string) => {
    navigate(`/dashboard/chat/${id}`);
  };

  const handleViewDetails = (id: string) => {
    const data = maps[id];

    if (!data || data.onsiteInterviewStatus !== OnsiteInterviewStatus.ActionNeeded) {
      navigate(`/dashboard/job/${id}`);
      return;
    }

    if (
      data.onsiteInterviewStatus === OnsiteInterviewStatus.ActionNeeded &&
      data.candidatesAppliedCount > 1
    ) {
      navigate(`/dashboard/job/${id}/confirm/${professionalId}`);
      return;
    }

    if (data.counterOffer) {
      handleConfirmInterview(id, data.counterOffer);
    }
  };

  const handleCloseDeclineSuccessPopup = () => {
    handleCloseDeclineInterview();
    navigate(`/permanent-jobs/job-overview/${postingId}`, { replace: true });
  };

  return (
    <>
      <Column backgroundColor={Colors.neutral_50} borderRadius={8} paddingBlock={24}>
        <Text bold color={Colors.primary_500} fontSize={20} marginBottom={14} marginInline={28}>
          Onsite Interviews:
        </Text>

        <Accordion
          SeparatorComponent={() => (
            <Box paddingInline={28}>
              <Divider color={Colors.neutral_150} height={1} marginVertical={12} />
            </Box>
          )}
          defaultOpenKey={defaultOpenKey}
        >
          {!isTerminalStatus && (
            <AccordionItem
              itemKey="available"
              title={
                <Title
                  color={Colors.success_500}
                  title="Available Interviews"
                  count={invitableInterviews.length}
                />
              }
              description={
                officeStatus === CandidateStatus.AppExpired
                  ? // eslint-disable-next-line max-len
                    `This professional's applications have expired, but you can still invite ${professionalName} to other available onsite interview slots.`
                  : `Invite ${professionalName} to available onsite interview slots.`
              }
              style={{
                paddingInline: 28,
              }}
            >
              {invitableInterviews.length === 0 ? (
                <Text color={Colors.neutral_400} fontSize={14} textAlign="center">
                  No available interviews.
                </Text>
              ) : (
                invitableInterviews.map((interview) => (
                  <OnsiteInterviewCard
                    candidatesAppliedCount={interview.candidatesAppliedCount}
                    date={interview.date}
                    endsAt={interview.endTime}
                    key={interview.onsiteInterviewId}
                    isInvited={interview.isInvited}
                    isInviting={getIsInvitingById(interview.onsiteInterviewId)}
                    onsiteInterviewId={interview.onsiteInterviewId}
                    profession={interview.profession}
                    rate={interview.rate}
                    specialty={
                      EducationUtils.removeSpecialtyPrefix(interview.specialty) || 'General'
                    }
                    startsAt={interview.startTime}
                    variant="available"
                    onInvite={handleInvite}
                    onViewDetails={handleViewDetails}
                  />
                ))
              )}
            </AccordionItem>
          )}
          {!isTerminalStatus && (
            <AccordionItem
              itemKey="applied"
              title={<Title color={Colors.error_450} title="Applied" count={appliedIds.length} />}
              style={{
                paddingInline: 28,
              }}
            >
              {appliedIds.length === 0 ? (
                <Text color={Colors.neutral_400} fontSize={14} textAlign="center">
                  No applications yet.
                </Text>
              ) : (
                appliedIds.map((id) => {
                  const interview = maps[id];
                  if (!interview) return null;
                  return (
                    <OnsiteInterviewCard
                      candidatesAppliedCount={interview.candidatesAppliedCount}
                      date={interview.localDate}
                      endsAt={interview.localEndTime}
                      key={id}
                      onsiteInterviewId={id}
                      profession={interview.profession}
                      rate={interview.rate}
                      specialty={
                        EducationUtils.removeSpecialtyPrefix(interview.specialty) || 'General'
                      }
                      startsAt={interview.localStartTime}
                      variant="applied"
                      onConfirm={(id) =>
                        interview.counterOffer && handleConfirmInterview(id, interview.counterOffer)
                      }
                      onDecline={(id) =>
                        interview.counterOffer && handleDeclineInterview(id, interview.counterOffer)
                      }
                      onViewDetails={handleViewDetails}
                    />
                  );
                })
              )}
            </AccordionItem>
          )}
          {!isTerminalStatus && (
            <AccordionItem
              itemKey="scheduled"
              title={
                <Title color={Colors.secondary_500} title="Scheduled" count={scheduledIds.length} />
              }
              style={{
                paddingInline: 28,
              }}
            >
              {scheduledIds.length === 0 ? (
                <Text color={Colors.neutral_400} fontSize={14} textAlign="center">
                  No scheduled interviews.
                </Text>
              ) : (
                scheduledIds.map((id) => {
                  const interview = maps[id];
                  if (!interview) return null;
                  return (
                    <OnsiteInterviewCard
                      date={interview.localDate}
                      endsAt={interview.localEndTime}
                      key={id}
                      onsiteInterviewId={id}
                      profession={interview.profession}
                      startsAt={interview.localStartTime}
                      specialty={
                        EducationUtils.removeSpecialtyPrefix(interview.specialty) || 'General'
                      }
                      rate={interview.rate}
                      variant="scheduled"
                      onMessage={handleMessageClick}
                      onViewDetails={handleViewDetails}
                    />
                  );
                })
              )}
            </AccordionItem>
          )}

          <AccordionItem
            itemKey="completed"
            title={
              <Title color={Colors.neutral_300} title="Completed" count={completedIds.length} />
            }
            style={{
              paddingInline: 28,
            }}
          >
            {completedIds.length === 0 ? (
              <Text color={Colors.neutral_400} fontSize={14} textAlign="center">
                No completed interviews.
              </Text>
            ) : (
              completedIds.map((id) => {
                const interview = maps[id];
                if (!interview) return null;
                return (
                  <OnsiteInterviewCard
                    date={interview.localDate}
                    endsAt={interview.localEndTime}
                    key={id}
                    onsiteInterviewId={id}
                    profession={interview.profession}
                    rate={interview.rate}
                    specialty={
                      EducationUtils.removeSpecialtyPrefix(interview.specialty) || 'General'
                    }
                    startsAt={interview.localStartTime}
                    variant="completed"
                    onViewDetails={handleViewDetails}
                  />
                );
              })
            )}
          </AccordionItem>
        </Accordion>
      </Column>

      {isConfirmOpen && confirmInterviewId && maps[confirmInterviewId]?.counterOffer && (
        <InterviewDetailsModal
          counterOffer={maps[confirmInterviewId].counterOffer!}
          interviewId={confirmInterviewId}
          isLoading={isConfirming}
          professionalId={professionalId}
          onClose={handleCloseConfirmInterview}
          onConfirm={handleConfirmCandidateInterview}
        />
      )}

      {isDeclineOpen && !isDeclineSuccess && (
        <DeclineCandidateConfirmationPopup
          isLoading={isDeclining}
          jobLocalDate={maps[declineInterviewId || '']?.localDate}
          professionalName={professionalName}
          onClose={handleCloseDeclineInterview}
          onConfirm={handleConfirmDeclineInterview}
        />
      )}

      {isDeclineSuccess && (
        <DeclineCandidateSuccessPopup
          professionalName={professionalName}
          onClose={handleCloseDeclineSuccessPopup}
        />
      )}
    </>
  );
};
